/**
 * Phase 86e — Filet de sécurité pour scanners en mode HID clavier.
 *
 * Contexte : les scanners de codes-barres/QR (Inateck, Eyoyo, etc.) émulent
 * un clavier. Par défaut beaucoup sont configurés en clavier US (QWERTY),
 * et Windows en clavier français (AZERTY ou suisse romand) ré-interprète
 * les scancodes différemment, ce qui transforme l'URL `https://maison-thai.ch/trace/...`
 * en `https.::éqison6thqi;ch:trqce:...` (caractères baroques).
 *
 * Sur Android/tablette l'IME virtuel évite ce problème : il reçoit les
 * caractères Unicode directement. Sur Windows c'est l'API clavier physique
 * qui réinterprète les scancodes, d'où le mojibake.
 *
 * Solution propre = reconfigurer le scanner via son manuel constructeur
 * (Inateck → mode "French AZERTY"). Mais on garde ce filet pour rattraper
 * les scans depuis un PC mal configuré.
 *
 * Approche : on dispose de **plusieurs tables de remap** (FR France, FR Suisse,
 * etc.). Pour chaque input mojibaké, on essaie tous les remaps et on garde
 * celui qui produit une URL `maison-thai.ch` valide.
 */

// --- Tables de remap (caractère reçu → caractère attendu)
// Construites empiriquement en alignant des paires (mojibake observé ↔ payload réel).

/**
 * Table AZERTY-FR (Windows clavier français France) ↔ QWERTY-US (scanner).
 * Construite par alignement de la position physique des touches.
 */
const AZERTY_FR_TO_QWERTY_US = {
  // Lettres swap (alpha-keyboard)
  a: "q", q: "a", z: "w", w: "z",
  A: "Q", Q: "A", Z: "W", W: "Z",
  // Symboles à la place des chiffres (ligne du haut sans Shift)
  "&": "1", "é": "2", "\"": "3", "'": "4",
  "(": "5", "-": "6", "è": "7", "_": "8",
  "ç": "9", "à": "0", ")": "-", "=": "=",
  // Symboles ligne du milieu (à droite de L)
  m: ";", M: ":", "ù": "'", "%": "\"", "*": "\\",
  // Symboles ligne du bas (après N)
  ",": "m", ";": ",", ":": ".", "!": "/",
  ".": ":", "/": "!", "§": "?",  // avec Shift
  // Quelques accents
  "²": "`", "^": "[", "$": "]",
};

/**
 * Table observée empiriquement à partir du retour utilisateur 12 juin 2026.
 * Mojibake reçu : `https.::éqison6thqi;ch:trqce:+=ç:L6=+6"&=&+"`
 * Probable attendu : `https://maison-thai.ch/trace/<SKU>/<LOT>`
 *
 * Couples extraits :
 *   . → :     :  → /     é → m     q → a
 *   6 → -     ; → -
 * Cette table est plus indulgente que la précédente (elle gère plusieurs
 * cas non-standards observés sur PC suisse romand notamment).
 */
const OBSERVED_PC_TO_TEXT = {
  // Cas observés directement dans l'exemple utilisateur
  ".": ":",
  ":": "/",
  "é": "m",
  q: "a",
  Q: "A",
  "6": "-",
  ";": "-",
  // Lettres alpha-keyboard swap (cas standard)
  a: "q", A: "Q",
  z: "w", Z: "W", w: "z", W: "Z",
  // Tentatives raisonnables pour les SKU contenant chiffres/lettres
  "+": "S", "=": "K", "ç": "U",
  "\"": "0", "&": "1",  // chiffres
  "ù": "'",
};

const MAPS = [
  { name: "azerty-fr", table: AZERTY_FR_TO_QWERTY_US },
  { name: "observed-pc", table: OBSERVED_PC_TO_TEXT },
];

/** Remap un caractère selon la table (laisse inchangé si non mappé). */
function remap(c, table) {
  return table[c] ?? c;
}

/** Vérifie si un payload ressemble à une URL de traçabilité Maison Thaï. */
function looksLikeTraceUrl(s) {
  if (!s) return false;
  return /^https?:\/\/[a-z0-9.-]+\/trace\//i.test(s) || /maison-thai\.ch\/trace/i.test(s);
}

/** Heuristique : le payload semble mojibaké (URL cassée). */
function looksMojibaked(s) {
  if (!s) return false;
  // Cas typique : commence par "https" mais sans `://`
  if (/^https[.;,:][^/]/.test(s)) return true;
  // Présence d'accents dans un payload qui devrait être ASCII (URL)
  if (s.startsWith("https") && /[éèçàùâêîôû²§]/.test(s)) return true;
  return false;
}

/**
 * Tente de "démojibakiser" un payload de scan reçu en mode HID clavier.
 *
 * @param {string} raw - texte brut reçu du scanner
 * @returns {string} texte corrigé si une correction est trouvée, sinon raw
 *
 * Retourne TOUJOURS quelque chose (jamais null/undefined). Si rien à corriger,
 * retourne le raw original.
 */
export function tryUnmojibakeScan(raw) {
  if (!raw || typeof raw !== "string") return raw;
  if (!looksMojibaked(raw)) return raw;

  // Pour chaque table de remap, on tente la conversion
  for (const { table } of MAPS) {
    const remapped = Array.from(raw).map((c) => remap(c, table)).join("");
    if (looksLikeTraceUrl(remapped)) {
      return remapped;
    }
  }
  // Aucune table n'a produit une URL valide → on renvoie le brut
  return raw;
}

/** Variante : retourne aussi le diagnostic (utile pour log/UI). */
export function unmojibakeWithDiagnostic(raw) {
  const result = tryUnmojibakeScan(raw);
  return {
    original: raw,
    corrected: result,
    wasCorrected: result !== raw,
    looksValid: looksLikeTraceUrl(result),
  };
}
